import React, { useMemo } from 'react'
import { Alert, Button, Checkbox, Input, Segmented, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons'
import { DashboardCatalogGroup } from '../../../svetofor-dashboard/api'
import {
  changeGroupAccess,
  changeIndicatorAccess,
  getSelectedIndicatorIds,
  russianCount,
} from '../utils/roleDashboardAccess'
import { DashboardAccessPanel } from './RoleDashboardAccessEditor.styles'
import { RoleSettingsSection } from './RoleSettingsSection'

import { RoleDashboardAccessModel } from './useRoleDashboardAccess'

interface RoleDashboardAccessEditorProps {
  model: RoleDashboardAccessModel
  saving: boolean
  roleId?: number
}

export const RoleDashboardAccessEditor = ({
  roleId,
  model,
  saving,
}: RoleDashboardAccessEditorProps) => {
  const {
    catalog,
    grants,
    setGrants,
    loading,
    loadError,
    search,
    setSearch,
    onlySelected,
    setOnlySelected,
    expandedGroups,
    setExpandedGroups,
    reload,
  } = model
  const counts = useMemo(
    () => new Map(catalog.map(group => [group.id, getSelectedIndicatorIds(group, grants).length])),
    [catalog, grants],
  )
  const selectedGroupCount = catalog.filter(group => (counts.get(group.id) || 0) > 0).length
  const selectedIndicatorCount = [...counts.values()].reduce((total, count) => total + count, 0)
  const disabled = loading || saving || Boolean(loadError)
  const query = search.trim().toLowerCase()
  const filteredGroups = catalog.filter(
    group =>
      (!onlySelected || (counts.get(group.id) || 0) > 0) &&
      (!query ||
        group.name.toLowerCase().includes(query) ||
        group.indicators.some(item => item.name.toLowerCase().includes(query))),
  )

  const toggleGroup = (group: DashboardCatalogGroup, checked: boolean) => {
    setGrants(current => changeGroupAccess(current, group, checked ? 'all' : 'none'))
  }

  const toggleIndicator = (group: DashboardCatalogGroup, indicatorId: number, checked: boolean) => {
    setGrants(current => changeIndicatorAccess(current, group, indicatorId, checked))
  }

  const toggleExpanded = (groupId: number) =>
    setExpandedGroups(current => {
      const next = new Set(current)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })

  const changeSearch = (value: string) => {
    setSearch(value)
    const nextQuery = value.trim().toLowerCase()
    if (nextQuery)
      setExpandedGroups(
        new Set(
          catalog
            .filter(
              group =>
                group.name.toLowerCase().includes(nextQuery) ||
                group.indicators.some(item => item.name.toLowerCase().includes(nextQuery)),
            )
            .map(group => group.id),
        ),
      )
  }

  return (
    <RoleSettingsSection
      title="Сводная Светофора"
      description="Выберите группы и отдельные показатели, которые будут видны сотрудникам с этой ролью."
      summary={
        roleId ? (
          <>
            {russianCount(selectedGroupCount, ['группа', 'группы', 'групп'])} ·{' '}
            {russianCount(selectedIndicatorCount, ['показатель', 'показателя', 'показателей'])}
          </>
        ) : undefined
      }
    >
      {!roleId ? (
        <Alert
          type="info"
          showIcon
          message="Выбор групп и показателей станет доступен после создания роли."
        />
      ) : (
        <DashboardAccessPanel>
          <div className="role-toolbar">
            <Input
              aria-label="Поиск показателей Светофора"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Найти группу или показатель"
              value={search}
              disabled={loading || saving}
              onChange={event => changeSearch(event.target.value)}
            />
            <Segmented
              aria-label="Фильтр групп"
              disabled={disabled}
              value={onlySelected ? 'selected' : 'all'}
              onChange={value => setOnlySelected(value === 'selected')}
              options={[
                { label: 'Все группы', value: 'all' },
                { label: `Выбрано ${selectedGroupCount}`, value: 'selected' },
              ]}
            />
          </div>
          {loadError && (
            <Alert
              className="access-alert"
              type="error"
              showIcon
              message={loadError}
              action={
                <Button htmlType="button" size="small" onClick={reload}>
                  Загрузить заново
                </Button>
              }
            />
          )}

          <div className="access-columns" aria-hidden="true">
            <span>Группа</span>
            <span>Показатели</span>
          </div>
          <Spin spinning={loading}>
            <div className="access-list">
              {filteredGroups.map(group => {
                const wholeGroup = grants.some(grant => grant.groupId === group.id && grant.all)
                const count = counts.get(group.id) || 0
                const expanded = expandedGroups.has(group.id)
                const selectedIds = new Set(getSelectedIndicatorIds(group, grants))
                const indicators =
                  !query || group.name.toLowerCase().includes(query)
                    ? group.indicators
                    : group.indicators.filter(item => item.name.toLowerCase().includes(query))
                const contentId = `svetofor-role-${roleId}-group-${group.id}`
                return (
                  <div key={group.id} className={`access-group${expanded ? ' is-expanded' : ''}`}>
                    <div className="access-row">
                      <div className="access-group-name">
                        <button
                          type="button"
                          className="access-expand"
                          aria-label={`Показатели группы «${group.name}»`}
                          aria-expanded={expanded}
                          aria-controls={contentId}
                          disabled={disabled}
                          onClick={() => toggleExpanded(group.id)}
                        >
                          <RightOutlined />
                        </button>
                        <Checkbox
                          aria-label={`Вся группа «${group.name}»`}
                          checked={count === group.indicators.length && count > 0}
                          indeterminate={count > 0 && count < group.indicators.length}
                          disabled={disabled}
                          onChange={event => toggleGroup(group, event.target.checked)}
                        />
                        <button
                          type="button"
                          className="access-group-title"
                          title={group.name}
                          aria-expanded={expanded}
                          aria-controls={contentId}
                          disabled={disabled}
                          onClick={() => toggleExpanded(group.id)}
                        >
                          {group.name}
                        </button>
                      </div>
                      <span className="access-count">
                        {wholeGroup ? (
                          <Tooltip
                            title="Будут доступны все текущие и новые показатели группы."
                            trigger={['hover', 'focus']}
                          >
                            <button
                              type="button"
                              className="access-future"
                              aria-label="Вся группа включает новые показатели"
                            >
                              <InfoCircleOutlined />
                            </button>
                          </Tooltip>
                        ) : (
                          <span className="access-future-placeholder" aria-hidden="true" />
                        )}
                        {count} из {group.indicators.length}
                      </span>
                    </div>
                    {expanded && (
                      <div id={contentId} className="access-indicators">
                        {indicators.map(indicator => (
                          <Checkbox
                            key={indicator.id}
                            checked={selectedIds.has(indicator.id)}
                            disabled={disabled}
                            onChange={event =>
                              toggleIndicator(group, indicator.id, event.target.checked)
                            }
                          >
                            {indicator.name}
                          </Checkbox>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
              {!loading && !loadError && !filteredGroups.length && (
                <div className="access-empty">
                  <p>
                    {query
                      ? 'Группы и показатели не найдены'
                      : onlySelected
                      ? 'Для этой роли пока ничего не выбрано'
                      : 'Нет доступных групп показателей'}
                  </p>
                  {onlySelected && (
                    <Button htmlType="button" type="link" onClick={() => setOnlySelected(false)}>
                      Показать все группы
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Spin>
          <div className="access-explanation">
            Галочка группы открывает все её текущие и новые показатели. Для нескольких ролей доступ
            складывается.
          </div>
        </DashboardAccessPanel>
      )}
    </RoleSettingsSection>
  )
}
